import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Checkbox,
    ClickAwayListener,
    TextField,
    Portal,
    Popper
} from '@mui/material';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const MultiSelectDropdown = ({
    label,
    options = [],
    value = [],
    onChange,
    placeholder = "Select items",
    getOptionLabel = (option) => option.name || option.label || option,
    /** Optional: search against extra fields (e.g. email) while getOptionLabel stays short */
    getOptionSearchText = null,
    getOptionId = (option) => option.id || option.value || option,
    /** When true, keeps menu in DOM tree so ClickAway works inside scrollable tables */
    disablePortal = false,
    getOptionDisabled = () => false,
    isOptionEqualToValue = (option, value) => getOptionId(option) === getOptionId(value),
    fullWidth = true,
    disabled = false,
    sx = {},
    searchPlaceholder = "Search...",
    onBlur,
    onOpenChange,
    renderOption,
    error = false,
    dropdownIconSize = 24,
    ...props
}) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    const setOpenState = (nextOpen) => {
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
    };

    const handleToggle = () => {
        if (!disabled) {
            setOpenState(!open);
        }
    };

    const handleClickAway = () => {
        setOpenState(false);
        setSearchTerm('');
        if (onBlur) {
            setTimeout(() => {
                onBlur();
            }, 0);
        }
    };

    useEffect(() => {
        if (open && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [open]);

    const getSearchText = (option) =>
        (getOptionSearchText ? getOptionSearchText(option) : getOptionLabel(option)) || '';

    const filteredOptions = options
        .filter(option => getOptionId(option) !== 'select-all')
        .filter(option =>
            String(getSearchText(option)).toLowerCase().includes(searchTerm.toLowerCase())
        );
    
    // Reorder options: selected options first, then unselected options (maintaining original order within each group)
    const reorderedOptions = [
        ...filteredOptions.filter(option => value.some(item => isOptionEqualToValue(option, item))),
        ...filteredOptions.filter(option => !value.some(item => isOptionEqualToValue(option, item)))
    ];

    const handleOptionClick = (option) => {
        if (getOptionDisabled(option)) {
            return;
        }
        
        const optionId = getOptionId(option);
        
        if (optionId === 'select-all') {
            onChange && onChange(null, option);
            return;
        }
        
        const isSingleSelect = !Array.isArray(value) || (
            value.length <= 1 && 
            options.length > 0 && 
            getOptionId(options[0]) !== 'select-all'
        );
        
        if (isSingleSelect) {
            onChange && onChange(null, option);
            setOpenState(false);
        } else {
            const isSelected = value.some(item => isOptionEqualToValue(option, item));
            
            if (isSelected) {
                onChange && onChange(null, option);
            } else {
                onChange && onChange(null, option);
            }
        }
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSearchClick = (event) => {
        event.stopPropagation();
    };

    const getDisplayText = () => {
        const actualSelectedCount = value.filter(v => getOptionId(v) !== 'select-all').length;
        const totalCount = options.filter(opt => getOptionId(opt) !== 'select-all').length;
        
        if (actualSelectedCount === 0) {
            return placeholder || '';
        }
        return `(${actualSelectedCount}/${totalCount} selected)`;
    };

    const getSelectAllState = () => {
        const allSelectable = options.filter(opt => getOptionId(opt) !== 'select-all');
        const selectedCount = value.filter(v => getOptionId(v) !== 'select-all').length;
        
        return {
            isAllSelected: selectedCount === allSelectable.length && allSelectable.length > 0,
            isIndeterminate: selectedCount > 0 && selectedCount < allSelectable.length
        };
    };

    const defaultRenderOption = (props, option, { selected }) => {
        const optionId = getOptionId(option);
        const { isAllSelected, isIndeterminate } = getSelectAllState();
        
        const isChecked = optionId === 'select-all' 
            ? isAllSelected 
            : selected;
        
        const isIndeterminateState = optionId === 'select-all' && isIndeterminate;

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    fontSize: '12px'
                }}
            >
                <Checkbox
                    checked={isChecked}
                    indeterminate={isIndeterminateState}
                    size="small"
                    sx={{ marginRight: '8px', padding: 0 }}
                />
                <Typography sx={{ fontSize: '12px' }}>
                    {getOptionLabel(option)}
                </Typography>
            </Box>
        );
    };

    const finalRenderOption = renderOption || defaultRenderOption;

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box
                ref={containerRef}
                sx={{
                    position: 'relative',
                    width: fullWidth ? '100%' : 'auto',
                    ...sx
                }}
            >
                {/* Select Input */}
                <Box
                    onClick={handleToggle}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '5.57px 8px 5.57px 12px',
                        height: '37px',
                        border: error ? '1px solid #FF5630' : '1px solid #D1D5DB',
                        borderRadius: '8px',
                        backgroundColor: disabled ? '#F4F6F8' : '#fff',
                        cursor: disabled ? 'default' : 'pointer',
                        borderColor: error ? '#FF5630' : (disabled ? '#E5E7EB' : '#D1D5DB'),
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        '&:hover': {
                            borderColor: disabled ? '#E5E7EB' : '#D1D5DB',
                        },
                        '&:focus-within': {
                            borderColor: error ? '#FF5630' : '#3B82F6',
                            outline: 'none',
                        },
                    }}
                >
                    <div className='flex gap-2 items-center flex-1 min-w-0'>
                        <span className='!text-[14px] flex-shrink-0'
                            style={{ color: disabled ? '#919EAB' : (value.length > 0 ? '#212B36' : '#919EAB') }}
                        >{label}</span>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '12px !important',
                                color: disabled ? '#919EAB' : (value.length === 0 ? '#9CA3AF' : '#637381'),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                minWidth: 0
                            }}
                        >
                            {getDisplayText()}
                        </Typography>
                    </div>
                    <KeyboardArrowDownIcon
                        sx={{
                            color: disabled ? '#919EAB' : '#6B7280',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease-in-out',
                            flexShrink: 0,
                            marginLeft: '8px',
                            fontSize: dropdownIconSize,
                        }}
                    />
                </Box>

                {/* Dropdown Options - Using Popper for Portal */}
                <Popper
                    open={open}
                    anchorEl={containerRef.current}
                    placement="bottom-start"
                    disablePortal={disablePortal}
                    style={{ zIndex: disablePortal ? 2 : 1500, width: containerRef.current?.offsetWidth }}
                >
                    <Box
                        sx={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '4px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            marginTop: '4px',
                        }}
                    >
                        {/* Select All Option */}
                        {options.length > 0 && getOptionId(options[0]) === 'select-all' && (
                            <Box
                                onClick={() => handleOptionClick(options[0])}
                                sx={{
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    backgroundColor: '#F9FAFB',
                                    borderBottom: '1px solid #E5E7EB',
                                    '&:hover': {
                                        backgroundColor: '#F3F4F6',
                                    },
                                }}
                            >
                                {finalRenderOption(
                                    { 
                                        key: 'select-all',
                                        style: { fontSize: '12px' }
                                    }, 
                                    options[0], 
                                    { selected: false }
                                )}
                            </Box>
                        )}

                        {/* Search Input */}
                        <Box
                            sx={{
                                padding: '8px 12px',
                                borderBottom: '1px solid #E5E7EB',
                                backgroundColor: '#F9FAFB',
                            }}
                            onClick={handleSearchClick}
                        >
                            <TextField
                                ref={searchInputRef}
                                fullWidth
                                size="small"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onClick={handleSearchClick}
                                InputProps={{
                                    sx: {
                                        fontSize: '12px',
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: '1px solid #E5E7EB',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            border: '1px solid #D1D5DB',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            border: '1px solid #3B82F6',
                                        },
                                    }
                                }}
                                inputProps={{
                                    style: { fontSize: '12px' }
                                }}
                            />
                        </Box>

                        {/* Individual Options */}
                        {reorderedOptions.length > 0 ? (
                            reorderedOptions.map((option, index) => {
                                const optionId = getOptionId(option);
                                const isSelected = value.some(item => isOptionEqualToValue(option, item));
                                const isDisabled = getOptionDisabled(option);

                                return (
                                    <Box
                                        key={optionId}
                                        onClick={() => handleOptionClick(option)}
                                        sx={{
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            fontSize: '12px',
                                            opacity: isDisabled ? 0.5 : 1,
                                            borderBottom: index < reorderedOptions.length - 1 ? '1px solid #F3F4F6' : 'none',
                                            '&:hover': {
                                                backgroundColor: isDisabled ? 'transparent' : '#F9FAFB',
                                            },
                                        }}
                                    >
                                        {finalRenderOption(
                                            { 
                                                key: optionId,
                                                style: { fontSize: '12px' }
                                            }, 
                                            option, 
                                            { selected: isSelected }
                                        )}
                                    </Box>
                                );
                            })
                        ) : (
                            <Box
                                sx={{
                                    padding: '16px 12px',
                                    textAlign: 'center',
                                    color: '#6B7280',
                                    fontSize: '12px',
                                }}
                            >
                                No options found
                            </Box>
                        )}
                    </Box>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default MultiSelectDropdown;